import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { mdiCalendarBlankOutline } from "@mdi/js";
import { TextField } from "@mui/material";
import { DatePicker } from "./components/DatePicker";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * MiDatePickerInline - A custom inline date picker component with Material UI integration.
 *
 * This component provides an inline date picker interface with custom styling and functionality.
 * It features a clickable text field that opens a date picker dialog with configurable actions.
 *
 * @component
 * @param {object} props - The props to customize the component behavior.
 * @param {object} [props.slots] - Custom slots for overriding default components (textField, openPickerIcon, etc.).
 * @param {object} [props.slotsProps] - Props to pass to the custom slots.
 * @param {boolean} [props.error=false] - If true, displays the input field in an error state.
 * @param {function} [props.onReset] - Callback function triggered when resetting the date picker.
 * @param {string|null} [props.color=null] - Optional color for the input field and labels. Uses theme defaults if null.
 * @param {function} props.onChange - Callback function when the date value changes.
 * @param {Date|string|Moment|null} props.value - The current selected date value.
 * @param {string} props.label - Label text for the date picker input field.
 * @param {boolean} [props.required=false] - If true, marks the field as required.
 * @param {Array<string>} [props.actions=[]] - Action buttons to display in the picker dialog (e.g., "reset", "clear", "cancel", "accept").
 * @param {object} [props.rest] - Additional props passed to the underlying DatePicker component.
 *
 * @returns {JSX.Element} The rendered MiDatePickerInline component.
 *
 * @example
 * // Basic usage
 * <MiDatePickerInline
 *   label="Select Date"
 *   value={selectedDate}
 *   onChange={(newDate) => setSelectedDate(newDate)}
 *   onReset={() => setSelectedDate(null)}
 * />
 *
 * @example
 * // With error state and required field
 * <MiDatePickerInline
 *   label="Inspection Date"
 *   value={inspectionDate}
 *   onChange={handleDateChange}
 *   onReset={handleReset}
 *   error={!!dateError}
 *   required
 *   color={theme.palette.primary.main}
 *   actions={["reset", "cancel", "accept"]}
 * />
 */
export function MiDatePickerInline({
  slots,
  slotsProps,
  error = false,
  onReset = () => null,
  color = null,
  onChange,
  value,
  label,
  actions = [],
  required = false,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const onToggleOpen = (value) => {
    setOpen(value);
  };

  const inlineDatePickerProp = {
    slots: {
      textField: InlineDatePickerInput,
      openPickerIcon: DatePickerIcon,
      ...slots,
    },
    slotProps: {
      onReset: () => {
        onReset();
        onToggleOpen(false);
      },
      onToggleOpen,
      color,
      textField: {
        size: "small",
        label,
        required,
      },
      ...slotsProps,
    },
  };

  return (
    <DatePicker
      {...rest}
      inline
      label="Date"
      format="MM/DD/YY"
      error={error}
      onOpen={() => onToggleOpen(true)}
      onClose={() => onToggleOpen(false)}
      open={open}
      value={value}
      onChange={onChange}
      actions={actions}
      {...inlineDatePickerProp}
    />
  );
}

const InlineDatePickerInput = (params) => {
  const {
    InputProps,
    ownerState: {
      slotProps: { onToggleOpen, error, color },
    },
    InputLabelProps,
    ...rest
  } = params;

  const { classes } = useStyles({ error, color });

  return (
    <TextField
      {...rest}
      className={classes.inputField}
      size="small"
      onClick={() => {
        onToggleOpen(true);
      }}
      error={error}
      InputLabelProps={{
        className: classes.inputFieldLabel,
        ...InputLabelProps,
      }}
      InputProps={{
        inputProps: { className: classes.inputField },
        classes: {
          notchedOutline: classes.notchedOutline,
        },
        ...InputProps,
      }}
    />
  );
};

const DatePickerIcon = (props) => (
  <MiIcon path={mdiCalendarBlankOutline} {...props} size={0.75} />
);

const useStyles = makeStyles()((theme, { error = false, color = null }) => ({
  inputField: {
    cursor: "pointer",
    flex: 1,
    "&:focus-visible": {
      outline: "none",
    },
    "& .MuiInputBase-input": {
      color: error ? theme.palette.red.dark : color,
      borderColor: color,
    },
    "& .MuiTextField-root": {
      borderColor: color,
    },
    "& .MuiIconButton-root": {
      padding: 0,
      marginRight: -6,
    },
  },
  notchedOutline: {
    borderColor: color,
  },
  inputFieldLabel: {
    color: error ? theme.palette.red.dark : color,
    top: 2,
  },
}));
